import { Box } from "@mui/material";
import React, { useRef } from "react";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";

import ResultStatement from "./ResultStatement";
import IfStatement from "./IFStatement";
import useHoverAction from "../../hooks/useHoverAction";
import { FieldOptionsType, StatementType } from "../../types";
import DeleteButton from "../../components/DeleteButton";
import Tag from "./Tag";
import AddButton from "../../components/AddButton";

type StatementHandlerProps = {
    statement: StatementType;
    fieldOptions: FieldOptionsType[];
    addConditionIndicator: (targetStatement?: StatementType) => void;
    removeConditionIndicator: (
        indicatorId: string,
        targetStatement?: StatementType,
    ) => void;
    updateConditionIndicator: (
        indicatorId: string,
        key: string,
        value: string | number,
        targetStatement?: StatementType,
    ) => void;
    addResult: (targetStatement?: StatementType) => void;
    removeResult: (targetStatement?: StatementType) => void;
    updateResult: (
        value: { result?: string; isLiteral: boolean; sourceName?: string },
        targetStatement?: StatementType,
    ) => void;
    addChildStatement: (
        targetStatement?: StatementType,
        isElse?: boolean,
    ) => void;
    removeSelf?: (
        targetStatement?: StatementType,
        isTopLevelThenStatement?: boolean,
    ) => void;
    addElseResult: (targetStatement?: StatementType) => void;
    isElseStatement?: boolean;
    isTopLevelThenStatement?: boolean;
    isTopLevelElseStatement?: boolean;
    isElseTree?: boolean;
};

const StatementHandler: React.FC<StatementHandlerProps> = observer(
    ({
        statement,
        fieldOptions,
        addConditionIndicator,
        removeConditionIndicator,
        updateConditionIndicator,
        addResult,
        removeResult,
        updateResult,
        addElseResult,
        addChildStatement,
        removeSelf,
        isElseStatement,
        isTopLevelThenStatement,
        isTopLevelElseStatement,
        isElseTree,
    }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const { showAction, handlers } = useHoverAction(containerRef);

        const hasResult =
            typeof statement.result !== "undefined" ||
            statement.sourceName !== undefined;
        const hasThenStatement = typeof statement.then !== "undefined";
        const hasElseStatement = typeof statement.else !== "undefined";
        const hasConditions = !!statement.conditionIndicators?.length;
        const isMaxDepth = statement.level === 4;

        const showElseButtons =
            ((hasResult && hasConditions) ||
                (hasThenStatement && hasConditions)) &&
            !hasElseStatement &&
            !isMaxDepth;

        const indent = isTopLevelElseStatement ? 0 : statement.level || 0;
        // Spacing for the left margin of the statement
        const leftSpacing = theme.spacing(indent * 3);

        let tagAdjustment = Math.max(indent - 3, 0);

        // Results are nested within a statement, but we want the else tag to be aligned with the if tag
        if (isElseStatement && hasResult) {
            if (isElseTree && indent === 2) {
                tagAdjustment = Math.max(tagAdjustment - 2, -1);
            } else {
                tagAdjustment = Math.max(tagAdjustment - 3, -2);
            }
        }

        const elseTagSpacing = theme.spacing(
            isTopLevelElseStatement ? 0 : tagAdjustment * 3,
        );

        return (
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    marginLeft: leftSpacing,
                }}
            >
                {isElseStatement && (
                    <Tag text="ELSE" sx={{ marginLeft: elseTagSpacing }} />
                )}
                <Box
                    ref={containerRef}
                    {...handlers}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        gap: theme.spacing(2),
                        padding: theme.spacing(2, 1),
                        width: "99%",
                        border:
                            isTopLevelElseStatement || isTopLevelThenStatement
                                ? undefined
                                : `1px solid ${theme.palette.gray[200]}`,
                        borderRadius: 2,
                    }}
                >
                    {hasConditions && (
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                maxWidth: `calc(100% - ${theme.spacing(2)})`,
                                gap: theme.spacing(2),
                            }}
                        >
                            <Tag text="IF" sx={{ margin: theme.spacing(2) }} />

                            <Box
                                sx={{
                                    width: "100%",
                                    backgroundColor: theme.palette.gray[100],
                                    padding: theme.spacing(2),
                                    borderRadius: 2,
                                }}
                            >
                                {statement?.conditionIndicators?.map(
                                    (indicator, indicatorIndex) => {
                                        const showSeparator =
                                            indicatorIndex !==
                                            (statement.conditionIndicators
                                                ?.length || 1) -
                                                1;
                                        const canRemove =
                                            statement.conditionIndicators
                                                ?.length !== 1;
                                        return (
                                            <IfStatement
                                                hasError={statement.hasError}
                                                showSeparator={showSeparator}
                                                removeIndicator={
                                                    canRemove
                                                        ? () => {
                                                              removeConditionIndicator(
                                                                  indicator.id,
                                                                  statement,
                                                              );
                                                          }
                                                        : undefined
                                                }
                                                key={indicator.id}
                                                indicator={indicator}
                                                updateIndicator={(
                                                    key,
                                                    value,
                                                ) => {
                                                    updateConditionIndicator(
                                                        indicator.id,
                                                        key,
                                                        value,
                                                        statement,
                                                    );
                                                }}
                                                fieldOptions={fieldOptions}
                                            />
                                        );
                                    },
                                )}
                                <AddButton
                                    onClick={() =>
                                        addConditionIndicator(statement)
                                    }
                                >
                                    OR
                                </AddButton>
                                <Tag
                                    text="THEN"
                                    sx={{ marginTop: theme.spacing(2) }}
                                />
                            </Box>
                        </Box>
                    )}
                    {hasResult && (
                        <ResultStatement
                            statement={statement}
                            fieldOptions={fieldOptions}
                            removeResult={() =>
                                isTopLevelThenStatement
                                    ? removeResult(statement)
                                    : removeSelf?.(statement)
                            }
                            updateResult={(val) => updateResult(val, statement)}
                        />
                    )}
                    <Box sx={{ display: "flex", gap: theme.spacing(1) }}>
                        {!hasResult && !statement.then && (
                            <>
                                {!isMaxDepth && (
                                    <AddButton
                                        onClick={() =>
                                            !hasConditions
                                                ? addConditionIndicator(
                                                      statement,
                                                  )
                                                : addChildStatement(statement)
                                        }
                                    >
                                        IF
                                    </AddButton>
                                )}

                                <AddButton onClick={() => addResult(statement)}>
                                    RESULT
                                </AddButton>
                            </>
                        )}
                        {showElseButtons && (
                            <>
                                <AddButton
                                    onClick={() => addElseResult(statement)}
                                >
                                    ELSE
                                </AddButton>

                                <AddButton
                                    onClick={() =>
                                        addChildStatement(statement, true)
                                    }
                                >
                                    ELSE IF
                                </AddButton>
                            </>
                        )}
                    </Box>
                    {removeSelf && (
                        <DeleteButton
                            onDelete={() =>
                                removeSelf(statement, isTopLevelThenStatement)
                            }
                            showDelete={showAction}
                            sx={{
                                position: "absolute",
                                top: theme.spacing(1),
                                right: theme.spacing(1),
                            }}
                        />
                    )}
                </Box>
                {statement.then && (
                    <StatementHandler
                        statement={statement.then}
                        fieldOptions={fieldOptions}
                        addConditionIndicator={addConditionIndicator}
                        removeConditionIndicator={removeConditionIndicator}
                        updateConditionIndicator={updateConditionIndicator}
                        addResult={addResult}
                        removeResult={removeResult}
                        updateResult={updateResult}
                        addElseResult={addElseResult}
                        addChildStatement={addChildStatement}
                        removeSelf={removeSelf}
                        isElseTree={isElseTree}
                    />
                )}
                {statement.else && (
                    <StatementHandler
                        statement={statement.else}
                        fieldOptions={fieldOptions}
                        addConditionIndicator={addConditionIndicator}
                        removeConditionIndicator={removeConditionIndicator}
                        updateConditionIndicator={updateConditionIndicator}
                        addResult={addResult}
                        removeResult={removeResult}
                        updateResult={updateResult}
                        addElseResult={addElseResult}
                        addChildStatement={addChildStatement}
                        removeSelf={removeSelf}
                        isElseStatement
                        isElseTree={isElseTree}
                    />
                )}
            </Box>
        );
    },
);

export default StatementHandler;
